import { Inject, inject, Injectable, OnDestroy } from '@angular/core';
import { FeatureFlagService } from '@galaxy/partner';
import { AddressAPIService } from '@vendasta/address';
import { GlobalParticipantType, InboxApiService, PlatformLocation } from '@vendasta/conversation';
import { IAMService, Role, UserIdentifier } from '@vendasta/iamv2';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  take,
} from 'rxjs';
import { MARKETING_SERVICES_PIDS } from './conversation.constants';
import { isWideScreen } from './inbox-utils';
import {
  EXCLUDED_GEOGRAPHICAL_US_STATES,
  INBOX_EMAIL,
  INBOX_OPEN_DEFAULT,
  INBOX_SMS_A2P,
  MESSAGE_TEMPLATE_CARD,
  WEB_CHAT_FOR_PARTNER_INBOX,
  WEBCHAT_SETTING_CARD,
} from './inbox.constants';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  CONVERSATION_CONFIG_TOKEN,
  CONVERSATION_COUNTRY_TOKEN,
  CONVERSATION_GEOGRAPHICAL_STATE_TOKEN,
  CONVERSATION_GOOGLE_BUSINESS_MESSAGES_AVAILABLE_TOKEN,
  CONVERSATION_PLATFORM_LOCATION_TOKEN,
  CONVERSATION_SMS_ENABLED_TOKEN,
  CONVERSATION_WEB_CHAT_ENABLED_TOKEN,
  FEATURE_FLAG_TOKEN,
  PARTNER_ID_TOKEN,
  USER_ID_TOKEN,
  GROUP_ID_TOKEN,
} from './tokens';
import { GetAccountInfoResponse, ParticipantType, SmsService } from '@vendasta/smsv2';
import { HttpErrorResponse } from '@angular/common/http';
import { ParticipantService } from './participant.service';

const isSuperAdminFn = (partner: Role): boolean => !!partner?.attributes.attributes['is_super_admin']?.boolAttribute;

@Injectable()
export class InboxService implements OnDestroy {
  private readonly unsubscribe$$ = new Subject<void>();
  private readonly smsService = this.isBusinessApp ? inject(SmsService) : null;

  readonly iamUser$ = this.userId$.pipe(
    switchMap((userId) => this.iamService.getUser({ userId: userId } as UserIdentifier)),
  );

  readonly canAccessEmbeddedSettings$ = combineLatest([this.iamUser$, this.partnerId$]).pipe(
    map(([user, currentPartnerId]) => {
      if (!this.isPartnerCenter) return false;
      const { partnerId: userPartnerID, roles: { partner } = {} } = user;
      const isSuperAdmin = isSuperAdminFn(partner);
      return (currentPartnerId === userPartnerID && !!partner) || isSuperAdmin;
    }),
    shareReplay(1),
  );

  readonly showAvailabilityMessageSettings$ = combineLatest([this.iamUser$, this.partnerId$]).pipe(
    map(([user, currentPartnerId]) => {
      const { partnerId: userPartnerID, roles: { partner } = {} } = user;
      const isSuperAdmin = isSuperAdminFn(partner);
      return (
        ((currentPartnerId === userPartnerID && !!partner) || isSuperAdmin) &&
        MARKETING_SERVICES_PIDS.includes(currentPartnerId)
      );
    }),
    shareReplay(1),
  );

  private readonly featureFlags$ = this.partnerId$.pipe(
    switchMap((partnerId) => {
      return this.featureFlagService
        .batchGetStatus(partnerId, '', [
          INBOX_OPEN_DEFAULT,
          INBOX_SMS_A2P,
          WEBCHAT_SETTING_CARD,
          MESSAGE_TEMPLATE_CARD,
          WEB_CHAT_FOR_PARTNER_INBOX,
          INBOX_EMAIL,
        ])
        .pipe(take(1), shareReplay(1));
    }),
  );

  readonly partnerHasA2PAccess$ = this.featureFlags$.pipe(
    map((res) => res[INBOX_SMS_A2P]),
    distinctUntilChanged(),
    shareReplay(1),
  );

  readonly SMSNumber$ = this.accountGroupId$.pipe(
    switchMap((accountGroupId) => {
      if (!this.smsService || !accountGroupId) return of(null);
      return this.smsService.getAccountInfo({
        internalId: accountGroupId,
        type: ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP,
      });
    }),
    catchError((error: HttpErrorResponse) => {
      console.error('error to get account info:', error.message);
      return of({} as GetAccountInfoResponse);
    }),
    map((accountInfo) => accountInfo?.phoneNumber),
    distinctUntilChanged(),
    shareReplay(1),
  );

  constructor(
    private readonly featureFlagService: FeatureFlagService,
    private readonly participantService: ParticipantService,
    @Inject(ACCOUNT_GROUP_ID_TOKEN) private readonly accountGroupId$: Observable<string>,
    @Inject(CONVERSATION_COUNTRY_TOKEN) readonly country$: Observable<string>,
    @Inject(CONVERSATION_GEOGRAPHICAL_STATE_TOKEN) private readonly geographicalState$: Observable<string>,
    @Inject(PARTNER_ID_TOKEN) private readonly partnerId$: Observable<string>,
    @Inject(FEATURE_FLAG_TOKEN)
    @Inject(CONVERSATION_PLATFORM_LOCATION_TOKEN)
    readonly platformLocation: PlatformLocation,
    private readonly addressService: AddressAPIService,
    @Inject(CONVERSATION_SMS_ENABLED_TOKEN) private smsEnabled$: Observable<boolean>,
    @Inject(CONVERSATION_WEB_CHAT_ENABLED_TOKEN) private webChatEnabled$: Observable<boolean>,
    @Inject(CONVERSATION_GOOGLE_BUSINESS_MESSAGES_AVAILABLE_TOKEN)
    private googleBusinessMessagesAvailable$: Observable<boolean>,
    @Inject(USER_ID_TOKEN) private readonly userId$: Observable<string>,
    private iamService: IAMService,
    private readonly inboxApiService: InboxApiService,
    @Inject(GROUP_ID_TOKEN) private readonly groupId$: Observable<string>,
  ) {}

  hasAccessToInboxPage$ =
    inject(CONVERSATION_CONFIG_TOKEN)?.hasAccessToInboxPage$?.pipe(map((result) => !!result)) || of(true);
  allowSmbChatWithPartner$ =
    inject(CONVERSATION_CONFIG_TOKEN)?.allowSmbChatWithPartner$?.pipe(map((result) => !!result)) || of(false);

  /**
   * Check if account can access SMS feature.
   * SMS is only accessible for US/CA accounts.
   */
  get canAccessSMS$(): Observable<boolean> {
    const hasAccessByCountry$ = this.geographicalState$.pipe(
      switchMap((geographicalState) => this.hasAccessByCountry(geographicalState)),
    );
    return combineLatest([this.hasAccessToInboxPage$, hasAccessByCountry$, this.smsEnabled$]).pipe(
      map(([hasAccessToInboxPage, hasAccessByCountry, smsEnabled]) => {
        return hasAccessToInboxPage && hasAccessByCountry && smsEnabled;
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  reauthorizeGoogleAccount() {
    // TODO: (BREW-316) - Reauthorize Google Account
  }

  get canAccess$(): Observable<boolean> {
    return combineLatest([this.hasAccessToInboxPage$, this.hasAccessByCountry()]).pipe(
      map(([hasAccess, hasAccessByCountry]) => hasAccess && hasAccessByCountry),
      take(1),
      shareReplay(1),
    );
  }

  private hasAccessByCountry(accountGroupGeographicalState?: string): Observable<boolean> {
    // https://address-demo.apigateway.co/address.v1.Address/ListAllCountryOptions
    // Australia - AU / United Kingdom - GB
    const availableCountries = ['ca', 'us'];
    return this.country$.pipe(
      switchMap((accountGroupCountry) => {
        const country = accountGroupCountry?.toLowerCase() || '';
        if (!availableCountries?.includes(country)) {
          return of(false);
        }
        if (country === 'us' && accountGroupGeographicalState) {
          return this.hasAccessByUSState(accountGroupGeographicalState).pipe(
            take(1),
            map((hasAccessByUSState) => hasAccessByUSState),
          );
        }
        return of(true);
      }),
    );
  }

  /**
   * Check if account is a US account
   * If the Country is US and the state doesn't belong to a valid US state the validation would be false.
   */
  private isUsAccountWithSmsAccess$(accountGroupGeographicalState?: string): Observable<boolean> {
    return this.country$.pipe(
      switchMap((accountGroupCountry) => {
        const country = accountGroupCountry?.toLowerCase() || '';
        if (country === 'us' && accountGroupGeographicalState) {
          return this.hasAccessByUSState(accountGroupGeographicalState);
        }
        return of(false);
      }),
    );
  }

  private hasAccessByUSState(accountGroupState: string): Observable<boolean> {
    const state = accountGroupState?.toLowerCase() || '';
    return this.addressService.getCountryConfiguration('US', undefined).pipe(
      take(1),
      map((countryInfo) => {
        const filteredStateList = countryInfo?.zones?.reduce((accumulator, currentValue) => {
          if (!EXCLUDED_GEOGRAPHICAL_US_STATES.includes(currentValue?.code)) {
            return [...accumulator, currentValue?.code?.toLocaleLowerCase()];
          }
          return accumulator;
        }, []);
        return filteredStateList?.includes(state);
      }),
      catchError((error) => {
        console.error('Error getting country configuration:', error.message);
        return of(false);
      }),
    );
  }

  readonly showInboxViews$ = of(!this.isBusinessApp);

  readonly canAccessInboxGetStartedCard$ = combineLatest([
    this.smsEnabled$,
    this.googleBusinessMessagesAvailable$,
  ]).pipe(
    map(([smsEnable, googleBusinessMessagesEnable]) => {
      return smsEnable && googleBusinessMessagesEnable;
    }),
    distinctUntilChanged(),
  );

  readonly openInboxDefault$ = this.featureFlags$.pipe(
    map((featureFlags) => {
      return featureFlags[INBOX_OPEN_DEFAULT] && this.isPartnerCenter && !isWideScreen();
    }),
    shareReplay(1),
  );

  readonly canAccessWebChatSettings$ = combineLatest([this.featureFlags$, this.webChatEnabled$]).pipe(
    map(([featureFlags, webchatEnabled]) => {
      const canAccessBA = featureFlags[WEBCHAT_SETTING_CARD] && this.isBusinessApp && webchatEnabled;
      const canAccessPCC = featureFlags[WEB_CHAT_FOR_PARTNER_INBOX] && this.isPartnerCenter;
      return canAccessBA || canAccessPCC;
    }),
    shareReplay(1),
  );

  readonly canAccessEmailChannel$ = this.featureFlags$.pipe(
    map((featureFlags) => {
      return featureFlags[INBOX_EMAIL] && this.isBusinessApp;
    }),
  );

  readonly canAccessMessageTemplateSettingsCard$ = this.featureFlags$.pipe(
    map((featureFlags) => featureFlags[MESSAGE_TEMPLATE_CARD]),
    shareReplay(1),
  );

  readonly multiLocationEnabled$ = this.groupId$.pipe(
    switchMap((groupId) => {
      if (!groupId || !this.isBusinessApp) {
        return of({ configuration: { multiLocationEnabled: false } });
      }
      return this.inboxApiService
        .getConfiguration({
          subjectParticipant: {
            participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_GROUP,
            internalParticipantId: groupId,
          },
        })
        .pipe(catchError(() => of({ configuration: { multiLocationEnabled: false } })));
    }),
    map((config) => {
      return config.configuration.multiLocationEnabled ?? false;
    }),
    catchError(() => of(false)),
    shareReplay(1),
  );

  /**
   * check if the current platform location is the business app
   * @return {boolean}
   */
  get isBusinessApp(): boolean {
    return this.platformLocation === PlatformLocation.PLATFORM_LOCATION_BUSINESS_APP;
  }

  /**
   * check if the current platform location is the partner center
   * @return {boolean}
   */
  get isPartnerCenter(): boolean {
    return this.platformLocation === PlatformLocation.PLATFORM_LOCATION_PARTNER_CENTER;
  }

  /**
   * check if the current platform location is the sales center
   * @return {boolean}
   */
  get isSalesCenter(): boolean {
    return this.platformLocation === PlatformLocation.PLATFORM_LOCATION_SALES_CENTER;
  }

  /**
   * check if displaying the A2P card in business app inbox setting page
   * @return {Promise<boolean>}
   */
  async displayA2pCard$(): Promise<boolean> {
    const hasAccessByCountry$ = this.geographicalState$.pipe(
      switchMap((geographicalState) => this.hasAccessByCountry(geographicalState)),
    );

    return await firstValueFrom(
      combineLatest([
        this.hasAccessToInboxPage$,
        hasAccessByCountry$,
        this.partnerHasA2PAccess$,
        this.smsEnabled$,
      ]).pipe(
        map(([hasAccessToInboxPage, hasAccessByCountry, partnerHasA2PAccess, smsEnable]): boolean => {
          return this.isBusinessApp && hasAccessToInboxPage && hasAccessByCountry && partnerHasA2PAccess && smsEnable;
        }),
      ),
    );
  }

  async buildTemplateTrackProperties() {
    return {
      accountGroupId: await firstValueFrom(this.accountGroupId$),
      participantName: (await firstValueFrom(this.participantService.currentParticipant$)).name || '',
      partnerId: await firstValueFrom(this.partnerId$),
      location: PlatformLocation[this.platformLocation],
    };
  }

  async buildAiKnowledgeAppConfigurationUrl(widgetId: string): Promise<string> {
    const accountGroupId = await firstValueFrom(this.accountGroupId$);
    if (accountGroupId) {
      return `/account/location/${accountGroupId}/settings/widgets/${widgetId}/edit`;
    }
    return `/(inbox:inbox/widgets/${widgetId}/edit)`;
  }

  buildAppId(widgetId: string): string {
    return `APP-WEBCHAT-${widgetId}`;
  }

  ngOnDestroy(): void {
    this.unsubscribe$$.next();
    this.unsubscribe$$.complete();
  }
}
